// src/components/ui/LastUpdated.jsx
// Tiny "Last updated" badge for every page that displays fetched data.
//
// Renders the given ISO timestamp in the user's LOCAL date/time using
// Intl.DateTimeFormat. Browser auto-picks the user's timezone — no setup.
//
// Usage:
//   <LastUpdated value={accounts.adsense.lastFetch} />
//   <LastUpdated value={lastFetch} label="Data refreshed" />
//   <LastUpdated value={lastFetch} variant="inline" />   // smaller, no icon
//
// The component is deliberately stateless and renders nothing if `value` is
// falsy — so call sites don't need their own null checks.

import { Clock } from 'lucide-react';

// Format helper — same display logic everywhere.
//  - today        → "Today, 2:34 PM"
//  - yesterday    → "Yesterday, 2:34 PM"
//  - this year    → "Mon, Jun 12, 2:34 PM"
//  - older        → "Jun 12, 2024, 2:34 PM"
function formatLocal(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;

  const now    = new Date();
  const today  = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yest   = new Date(today.getTime() - 86400000);
  const dDate  = new Date(d.getFullYear(), d.getMonth(), d.getDate());

  const time = d.toLocaleTimeString(undefined, {
    hour:   'numeric',
    minute: '2-digit',
  });

  if (dDate.getTime() === today.getTime())   return `Today, ${time}`;
  if (dDate.getTime() === yest.getTime())    return `Yesterday, ${time}`;

  const sameYear = d.getFullYear() === now.getFullYear();
  const datePart = d.toLocaleDateString(undefined, sameYear
    ? { weekday: 'short', month: 'short', day: 'numeric' }
    : { month: 'short', day: 'numeric', year: 'numeric' });
  return `${datePart}, ${time}`;
}

export function LastUpdated({
  value,
  label = 'Last updated',
  variant = 'badge',
  className = '',
}) {
  const formatted = formatLocal(value);
  if (!formatted) return null;

  // Show absolute UTC on hover for power users who want the precise instant.
  const utcTitle = new Date(value).toISOString();

  if (variant === 'inline') {
    return (
      <span
        title={utcTitle}
        className={`text-xs text-muted-foreground ${className}`}
      >
        {label}: {formatted}
      </span>
    );
  }

  // Default: badge style — small pill with clock icon.
  return (
    <span
      title={utcTitle}
      className={`inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground bg-muted/40 px-2.5 py-1 rounded-full ${className}`}
    >
      <Clock className="h-3 w-3" />
      <span>{label}</span>
      <span className="text-foreground/80">{formatted}</span>
    </span>
  );
}

export default LastUpdated;
